/* @jsx h */
import {
  defineComponent,
  h,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type VNodeChild,
} from "vue";
import { useRoute } from "vue-router";
import { animate, spring } from "animejs";

type TrackedDesktopNavProps = {
  renderItems: () => VNodeChild;
  posMode?: boolean;
};

export const TrackedDesktopNav = defineComponent<TrackedDesktopNavProps>({
  name: "TrackedDesktopNav",
  props: ["renderItems", "posMode"],
  setup(props) {
    const route = useRoute();
    const navContainerRef = ref<HTMLElement | null>(null);
    const indicatorRef = ref<HTMLElement | null>(null);
    let scheduledFrame: number | undefined;
    let navResizeObserver: ResizeObserver | undefined;
    let waitingForFonts = false;

    const updateIndicator = (animated: boolean, retries = 8) => {
      const container = navContainerRef.value;
      const indicator = indicatorRef.value;
      if (!container || !indicator) return;

      const links = Array.from(
        container.querySelectorAll("a[href]"),
      ) as HTMLAnchorElement[];
      let activeElement: HTMLAnchorElement | null = null;
      let bestLength = 0;

      for (const link of links) {
        const href = link.getAttribute("href") ?? "";
        if (href && route.path.startsWith(href) && href.length > bestLength) {
          activeElement = link;
          bestLength = href.length;
        }
      }

      if (!activeElement) {
        if (retries > 0) {
          requestAnimationFrame(() => updateIndicator(animated, retries - 1));
        } else {
          indicator.style.opacity = "0";
        }
        return;
      }

      const activeRect = activeElement.getBoundingClientRect();
      if (activeRect.width === 0 && retries > 0) {
        requestAnimationFrame(() => updateIndicator(animated, retries - 1));
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const left = activeRect.left - containerRect.left;
      const width = activeRect.width;

      if (animated) {
        animate(indicator, {
          left,
          width,
          opacity: 1,
          ease: spring({ bounce: 0.45, duration: 500 }),
        });
      } else {
        indicator.style.left = `${left}px`;
        indicator.style.width = `${width}px`;
        indicator.style.opacity = "1";
      }
    };

    const scheduleIndicatorUpdate = (animated: boolean) => {
      if (scheduledFrame !== undefined) {
        cancelAnimationFrame(scheduledFrame);
      }

      void nextTick(() => {
        scheduledFrame = requestAnimationFrame(() => {
          scheduledFrame = requestAnimationFrame(() => {
            scheduledFrame = undefined;
            updateIndicator(animated);
          });
        });
      });
    };

    const updateWhenFontsAreReady = () => {
      const fonts = (
        document as Document & { fonts?: { ready: Promise<unknown> } }
      ).fonts;
      if (!fonts || waitingForFonts) return;

      waitingForFonts = true;
      void fonts.ready.finally(() => {
        waitingForFonts = false;
        scheduleIndicatorUpdate(false);
      });
    };

    watch(navContainerRef, (element) => {
      navResizeObserver?.disconnect();
      if (!element) return;

      scheduleIndicatorUpdate(false);
      updateWhenFontsAreReady();
      navResizeObserver = new ResizeObserver(() =>
        scheduleIndicatorUpdate(false),
      );
      navResizeObserver.observe(element);
    });
    watch(
      () => route.path,
      () => scheduleIndicatorUpdate(true),
    );

    onMounted(() => {
      scheduleIndicatorUpdate(false);
      updateWhenFontsAreReady();
    });

    onUnmounted(() => {
      if (scheduledFrame !== undefined) {
        cancelAnimationFrame(scheduledFrame);
      }
      navResizeObserver?.disconnect();
    });

    return () => (
      <div
        data-posmode={props.posMode}
        class="mx-auto flex w-auto flex-1 justify-between font-headline"
      >
        <div class="flex flex-1">
          <div
            ref={navContainerRef}
            class="relative mx-auto flex sm:space-x-0.5 2xl:space-x-2"
          >
            <div>{props.renderItems()}</div>
            <div
              ref={indicatorRef}
              class="pointer-events-none absolute bottom-0 h-[3px] rounded-full bg-primary"
              style="left: 0; width: 0; opacity: 0; bottom: -8px;"
            />
          </div>
        </div>
      </div>
    );
  },
});
